import { z } from 'zod';
import { pageSchema } from '../../http/pagination';

export const PHONE_PATTERN = /^\d{10,11}$/;

const phoneSearchSchema = z.string().regex(PHONE_PATTERN, 'Phone number must be 10-11 digits').optional();

// GET /advisors のクエリパラメータ
// - パラメータなし: 全件取得（ドロップダウン用）
// - page（必須）+ 検索条件（任意）: ページネーション付き絞り込み
export const listAdvisorsQuerySchema = z.union([
  z.object({}).strict(),
  z
    .object({
      page: pageSchema,
      name: z.string().max(100, 'Name must be 100 characters or less').optional(),
      phone: phoneSearchSchema,
    })
    .strict(),
]);
export type ListAdvisorsQuery = z.infer<typeof listAdvisorsQuerySchema>;

export const advisorSchema = z.object({
  id: z.number(),
  name: z.string(),
  phone: z.string(),
});
export type Advisor = z.infer<typeof advisorSchema>;

export const listAdvisorsResponseSchema = z.object({
  count: z.number(),
  advisors: z.array(advisorSchema),
});
export type ListAdvisorsResponse = z.infer<typeof listAdvisorsResponseSchema>;

const phoneSchema = z.string().regex(PHONE_PATTERN, 'Phone number must be 10-11 digits');

export const bulkCreateAdvisorItemSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be 100 characters or less'),
  phone: phoneSchema,
  rowKey: z.number().int().nonnegative(),
});
export type BulkCreateAdvisorItem = z.infer<typeof bulkCreateAdvisorItemSchema>;

export const bulkCreateAdvisorsBodySchema = z
  .array(bulkCreateAdvisorItemSchema)
  .min(1, 'At least one advisor is required')
  .max(10, 'advisors must be 10 or less');
export type BulkCreateAdvisorsBody = z.infer<typeof bulkCreateAdvisorsBodySchema>;

export const bulkCreateAdvisorsResultSchema = z.discriminatedUnion('resultType', [
  // 成功 (警告なし)
  z.object({
    resultType: z.literal('success'),
    value: advisorSchema,
    rowKey: z.number(),
  }),
  // 成功 (警告あり)
  z.object({
    resultType: z.literal('success-with-warning'),
    value: advisorSchema,
    reason: z.literal('same-name-exists'),
    existingRecords: z.array(advisorSchema),
    rowKey: z.number(),
  }),
  // エラー (入力データ内重複)
  z.object({
    resultType: z.literal('error-input-duplicate'),
    input: bulkCreateAdvisorItemSchema,
    reason: z.enum(['exact-duplicate', 'name-changed']),
    duplicateRows: z.array(
      z.object({
        name: z.string(),
        phone: z.string(),
        rowNumber: z.number(),
      })
    ),
    rowKey: z.number(),
  }),
  // エラー (既存データとの重複)
  z.object({
    resultType: z.literal('error-existing-duplicate'),
    input: bulkCreateAdvisorItemSchema,
    reason: z.enum(['exact-duplicate', 'name-changed']),
    existingRecords: z.array(advisorSchema),
    rowKey: z.number(),
  }),
]);
export type BulkCreateAdvisorsResult = z.infer<typeof bulkCreateAdvisorsResultSchema>;

export const bulkCreateAdvisorsResponseSchema = z.object({
  summary: z.object({
    success: z.number(),
    error: z.number(),
  }),
  results: z.array(bulkCreateAdvisorsResultSchema),
});
export type BulkCreateAdvisorsResponse = z.infer<typeof bulkCreateAdvisorsResponseSchema>;

export const updateAdvisorSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be 100 characters or less'),
  phone: phoneSchema,
});
export type UpdateAdvisorBody = z.infer<typeof updateAdvisorSchema>;

export const updateAdvisorResponseSchema = z.discriminatedUnion('resultType', [
  // 成功 (警告なし)
  z.object({
    resultType: z.literal('success'),
    value: advisorSchema,
  }),
  // 成功 (警告あり)
  z.object({
    resultType: z.literal('success-with-warning'),
    value: advisorSchema,
    reason: z.literal('same-name-exists'),
    existingRecords: z.array(advisorSchema),
  }),
  // エラー (既存データとの重複)
  z.object({
    resultType: z.literal('error'),
    input: updateAdvisorSchema,
    reason: z.enum(['exact-duplicate', 'name-changed']),
    existingRecords: z.array(advisorSchema),
  }),
]);
export type UpdateAdvisorResponse = z.infer<typeof updateAdvisorResponseSchema>;
